import { useEffect, useState } from "react";
import { useAuthStore } from "../store/authStore";
import { useRoleStore } from "../store/roleStore";

// Single source of truth for "which product roles does this user have, and
// do they have the BioInterpreter feature-flag role" — previously computed
// independently (and identically) in ReportProcessDialog.jsx and
// ProductDropDown.jsx, which is how InputLibrary.jsx's separate "Use in Run"
// dialog ended up with no gating at all: there was no shared place to reuse.
//
// `enabled` lets a caller that's normally hidden (e.g. a dialog that isn't
// open yet) defer the /role-list fetch until it actually needs it, matching
// ReportProcessDialog's existing lazy-fetch-on-open behavior. Callers that
// want the eager, fetch-on-mount behavior (ProductDropDown, InputLibrary)
// just omit it.
export function useUserProductAccess({ enabled = true } = {}) {
  const user = useAuthStore((state) => state.user);
  const { roles, isLoading, fetchRoles } = useRoleStore();

  const [isAdmin, setIsAdmin] = useState(false);
  const [productRoles, setProductRoles] = useState([]);
  const [hasBioInterpreter, setHasBioInterpreter] = useState(false);

  useEffect(() => {
    if (enabled) fetchRoles();
  }, [enabled, fetchRoles]);

  useEffect(() => {
    const userRoles = Array.isArray(user?.roles)
      ? user.roles
      : typeof user?.roles === "string" && user?.roles
      ? [user.roles]
      : [];

    if (isLoading || roles.length === 0) return;

    if (!userRoles.length) {
      setIsAdmin(false);
      setProductRoles([]);
      setHasBioInterpreter(false);
      return;
    }

    const allProductRoles = roles.filter((role) => role.type === "product");
    const admin = userRoles.includes("Admin");

    const userProductRoles = admin
      ? allProductRoles
      : allProductRoles.filter((role) => userRoles.includes(role.id));

    setIsAdmin(admin);
    setHasBioInterpreter(userProductRoles.some((role) => role.title === "BioInterpreter"));
    setProductRoles(userProductRoles.filter((role) => role.title !== "BioInterpreter"));
  }, [isLoading, roles, user]);

  return { isLoading, isAdmin, productRoles, hasBioInterpreter };
}
